"use client";

import { useEffect, useRef, useState } from "react";
import type { AiProfile } from "@/types/ai";
import { trackClarityMetric, trackReflection, trackInsight, trackOutcome } from "@/lib/analytics/clarityTracking";

interface Props {
  profile: AiProfile;
  userId?: string;
  messages: Array<{ role: "user" | "assistant"; content: string }>;
}

export default function ClarityTracker({ profile, userId = "default", messages }: Props) {
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const sessionStartRef = useRef<number>(Date.now());
  const [clarityScore, setClarityScore] = useState<number | null>(null);
  const [hasTrackedSession, setHasTrackedSession] = useState(false);
  const messageCountRef = useRef<number>(0);
  const topicsRef = useRef<Set<string>>(new Set());

  // Track session metrics periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (messages.length > 0 && !hasTrackedSession) {
        trackSessionMetrics();
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [messages.length, hasTrackedSession]);

  // Track when user messages contain reflection indicators
  useEffect(() => {
    const userMessages = messages.filter(m => m.role === "user");
    if (userMessages.length > messageCountRef.current) {
      const newMessages = userMessages.slice(messageCountRef.current);
      
      newMessages.forEach(msg => {
        // Detect reflection questions
        if (isReflectionQuestion(msg.content)) {
          trackReflection(profile.id, userId, {
            question: msg.content,
            answer: "", // Will be filled when assistant responds
          });
        }
        
        // Extract topics
        extractTopics(msg.content).forEach(topic => topicsRef.current.add(topic));
      });
      
      messageCountRef.current = userMessages.length;
    }
  }, [messages, profile.id, userId]);

  // Track insights from assistant messages
  useEffect(() => {
    const assistantMessages = messages.filter(m => m.role === "assistant");
    assistantMessages.forEach(msg => {
      const insights = extractInsights(msg.content);
      insights.forEach(insight => {
        trackInsight(profile.id, userId, {
          type: insight.type,
          content: insight.content,
          actionable: insight.actionable,
          confidence: insight.confidence,
        });
      });
    });
  }, [messages, profile.id, userId]);

  function trackSessionMetrics() {
    const sessionDuration = Date.now() - sessionStartRef.current;
    const userMessages = messages.filter(m => m.role === "user");
    const assistantMessages = messages.filter(m => m.role === "assistant");
    
    trackClarityMetric(profile.id, userId, {
      sessionId: sessionIdRef.current,
      reflectionCount: userMessages.filter(m => isReflectionQuestion(m.content)).length,
      insightsGenerated: assistantMessages.length,
      actionsTaken: 0, // Will be tracked separately
      sessionDuration,
      messagesExchanged: messages.length,
      topicsDiscussed: Array.from(topicsRef.current),
      clarityScore: clarityScore || undefined,
    });
    
    setHasTrackedSession(true);
  }

  function isReflectionQuestion(text: string): boolean {
    const reflectionKeywords = [
      "why", "what", "how", "when", "where",
      "should i", "can i", "what if", "how do i",
      "help me understand", "i'm confused", "i don't know",
      "i feel", "i think", "i want", "i need",
    ];
    
    const lowerText = text.toLowerCase();
    return reflectionKeywords.some(keyword => lowerText.includes(keyword));
  }

  function extractTopics(text: string): string[] {
    // Simple topic extraction - can be enhanced with NLP
    const topics: string[] = [];
    const commonTopics = [
      "career", "work", "job", "business", "project",
      "relationship", "family", "friends", "love",
      "health", "fitness", "mental health", "wellness",
      "finance", "money", "budget", "investment",
      "education", "learning", "study", "skill",
      "goal", "plan", "decision", "problem", "solution",
    ];
    
    const lowerText = text.toLowerCase();
    commonTopics.forEach(topic => {
      if (lowerText.includes(topic)) {
        topics.push(topic);
      }
    });
    
    return topics;
  }

  function extractInsights(text: string): Array<{
    type: "decision" | "plan" | "realization" | "next_step";
    content: string;
    actionable: boolean;
    confidence?: number;
  }> {
    const insights: Array<{
      type: "decision" | "plan" | "realization" | "next_step";
      content: string;
      actionable: boolean;
      confidence?: number;
    }> = [];
    
    // Look for actionable phrases
    const actionablePatterns = [
      { pattern: /you should|you could|try|consider|recommend/i, type: "next_step" as const },
      { pattern: /i suggest|i recommend|here's what|the best/i, type: "plan" as const },
      { pattern: /realize|understand|see that|it seems/i, type: "realization" as const },
      { pattern: /decide|choose|go with|pick/i, type: "decision" as const },
    ];
    
    actionablePatterns.forEach(({ pattern, type }) => {
      if (pattern.test(text)) {
        // Extract sentence containing the pattern
        const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
        const relevantSentences = sentences.filter(s => pattern.test(s));
        
        relevantSentences.forEach(sentence => {
          insights.push({
            type,
            content: sentence.trim(),
            actionable: type === "next_step" || type === "plan",
            confidence: 7, // Default confidence
          });
        });
      }
    });
    
    return insights;
  }

  // Expose function to track clarity score manually
  const trackClarityScore = (score: number) => {
    setClarityScore(score);
    trackClarityMetric(profile.id, userId, {
      sessionId: sessionIdRef.current,
      clarityScore: score,
    });
  };

  // Expose function to track outcome
  const trackUserOutcome = (achieved: boolean, description: string) => {
    const timeToClarity = Date.now() - sessionStartRef.current;
    trackOutcome(profile.id, userId, {
      achieved,
      description,
      clarityScoreBefore: clarityScore || undefined,
      clarityScoreAfter: achieved ? 10 : clarityScore || undefined,
      timeToClarity: achieved ? timeToClarity : undefined,
    });
  };

  // Store functions in window for external access (e.g., from chat interface)
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as typeof window & { clarityTracker?: { trackClarityScore: (score: number) => void; trackOutcome: (achieved: boolean, description: string) => void; getSessionId: () => string } }).clarityTracker = {
        trackClarityScore,
        trackOutcome: trackUserOutcome,
        getSessionId: () => sessionIdRef.current,
      };
    }
    
    return () => {
      if (typeof window !== "undefined") {
        delete (window as typeof window & { clarityTracker?: unknown }).clarityTracker;
      }
    };
  }, [profile.id, userId]);

  // Track final session metrics on unmount
  useEffect(() => {
    return () => {
      trackSessionMetrics();
    };
  }, []);

  return null; // This component doesn't render anything
}

